import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

// The executable skills of the LyraSkills service: what exists, what each needs, and a safe way to try the read-only
// ones. Data comes from Core's /api/skills-registry (the browser never talks to the skills service).

type Requirement = { capability: string; kinds: string[]; scopes: string[] };
type Skill = { id: string; name: string; description: string; category: string; status: 'available' | 'planned'; risk: 'read' | 'write' | 'send'; version: string; requirements: Requirement[]; flags: string[]; model: boolean };
type Doc = Skill & { inputs?: any; outputs?: any; credentials?: { required: boolean }; ['x-notes']?: string; providers?: string[]; idempotent?: boolean };

const card: React.CSSProperties = { background: 'var(--lyra-color-surface)', border: '0.5px solid var(--lyra-color-border)', borderRadius: 12 };
const chip = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg });
const RISK: Record<string, React.CSSProperties> = {
  read: chip('var(--app-primary-soft)', 'var(--app-primary-deep)'),
  write: chip('var(--lyra-color-warning-bg)', 'var(--lyra-color-warning)'),
  send: chip('var(--lyra-color-danger-bg)', 'var(--lyra-color-danger)'),
};
const STATUS: Record<string, React.CSSProperties> = {
  available: chip('var(--lyra-color-success-bg)', 'var(--lyra-color-success)'),
  planned: chip('var(--app-surface-3)', 'var(--lyra-color-text-muted)'),
};
const input: React.CSSProperties = { background: 'var(--lyra-color-surface)', color: 'var(--lyra-color-text)', border: '0.5px solid var(--app-border-strong)', borderRadius: 10, padding: '8px 10px', fontSize: 13 };

const errorText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback;

// A starting point for the Try-it box: required properties with a placeholder of the right type.
function example(schema: any): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const props = schema?.properties || {};
  for (const key of schema?.required || []) {
    const p = props[key] || {};
    const t = Array.isArray(p.type) ? p.type[0] : p.type;
    out[key] = p.default ?? (p.enum ? p.enum[0] : t === 'array' ? [] : t === 'object' ? {} : t === 'number' || t === 'integer' ? 1 : t === 'boolean' ? false : '');
  }
  return out;
}

function PropertyTable({ schema, title }: { schema: any; title: string }) {
  const props = Object.entries<any>(schema?.properties || {});
  if (!props.length) return null;
  const required = new Set<string>(schema.required || []);
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--lyra-color-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{title}</div>
      <div style={{ ...card, overflow: 'hidden' }}>
        {props.map(([name, p], i) => (
          <div key={name} style={{ display: 'grid', gridTemplateColumns: '150px 90px 1fr', gap: 10, padding: '8px 12px', borderTop: i ? '0.5px solid var(--lyra-color-border)' : 'none', fontSize: 12.5 }}>
            <code style={{ color: 'var(--lyra-color-text)' }}>{name}{required.has(name) ? ' *' : ''}</code>
            <span style={{ color: 'var(--lyra-color-text-muted)' }}>{p.type ? [].concat(p.type).join(' | ') : p.enum ? 'enum' : p.oneOf ? 'one of' : 'any'}</span>
            <span style={{ color: 'var(--app-text-2)' }}>{p.description || (p.enum ? p.enum.join(', ') : '')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Detail({ id, onClose }: { id: string; onClose: () => void }) {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('{}');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    let live = true;
    setDoc(null); setError(null); setResult(null);
    api.get(`/api/skills-registry/${encodeURIComponent(id)}`)
      .then((r) => { if (!live) return; setDoc(r.data.skill); setText(JSON.stringify(example(r.data.skill.inputs), null, 2)); })
      .catch((e) => live && setError(errorText(e, 'Could not load the skill')));
    return () => { live = false; };
  }, [id]);

  const runnable = !!doc && doc.status === 'available' && doc.risk === 'read' && !doc.credentials?.required && !(doc as any).model?.required && !(doc as any).flags?.length;
  const run = async () => {
    let inputs: any;
    try { inputs = JSON.parse(text); } catch { setResult({ error: 'The inputs are not valid JSON.' }); return; }
    setBusy(true); setResult(null);
    try { setResult((await api.post(`/api/skills-registry/${encodeURIComponent(id)}/run`, { inputs })).data); }
    catch (e) { setResult({ error: errorText(e, 'The run failed') }); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-label="Skill details" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: 'var(--lyra-color-surface-2)', borderLeft: '0.5px solid var(--lyra-color-border)', boxShadow: '-12px 0 32px rgba(0,0,0,.18)', overflowY: 'auto', zIndex: 60, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--lyra-color-text)' }}>{doc?.name || id}</div>
          <code style={{ fontSize: 12, color: 'var(--lyra-color-text-muted)' }}>{id}</code>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ ...input, cursor: 'pointer', padding: '4px 10px' }}>✕</button>
      </div>
      {error && <div style={{ marginTop: 14, color: 'var(--lyra-color-danger)', fontSize: 13 }}>{error}</div>}
      {!doc && !error && <div style={{ marginTop: 14, color: 'var(--lyra-color-text-muted)' }}>Loading…</div>}
      {doc && (
        <>
          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            <span style={STATUS[doc.status]}>{doc.status}</span><span style={RISK[doc.risk]}>risk: {doc.risk}</span>
            {doc.idempotent === false && <span style={chip('var(--app-surface-3)', 'var(--lyra-color-text-muted)')}>not idempotent</span>}
            {(doc as any).model?.required && <span style={chip('var(--app-primary-soft)', 'var(--app-primary-deep)')}>needs a model</span>}
            {(doc.providers || []).map((p) => <span key={p} style={chip('var(--app-surface-3)', 'var(--app-text-2)')}>{p}</span>)}
          </div>
          <p style={{ marginTop: 12, color: 'var(--app-text-2)', fontSize: 14, lineHeight: 1.5 }}>{doc.description}</p>
          {doc.status === 'planned' && <div style={{ ...card, padding: 12, marginTop: 10, fontSize: 13, color: 'var(--lyra-color-text-muted)' }}>Planned: registered, not executable yet. It has no inputs or outputs until it is built.</div>}
          {!!(doc as any).credentials?.requirements?.length && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--lyra-color-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Needs your connection</div>
              {(doc as any).credentials.requirements.map((r: Requirement) => (
                <div key={r.capability} style={{ ...card, padding: '8px 12px', marginBottom: 6, fontSize: 13 }}>
                  <code>{r.capability}</code> <span style={{ color: 'var(--lyra-color-text-muted)' }}>({r.kinds.join(' or ')}{r.scopes?.length ? `; ${r.scopes.join(', ')}` : ''})</span>
                </div>
              ))}
            </div>
          )}
          {(doc as any)['x-notes'] && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--lyra-color-text-muted)' }}>{(doc as any)['x-notes']}</div>}
          <PropertyTable schema={doc.inputs} title="Inputs (* required)" />
          <PropertyTable schema={doc.outputs} title="Outputs" />
          {runnable ? (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--lyra-color-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Try it</div>
              <textarea aria-label="Skill inputs (JSON)" value={text} onChange={(e) => setText(e.target.value)} rows={8} spellCheck={false}
                style={{ ...input, width: '100%', fontFamily: 'var(--lyra-font-mono)', fontSize: 12.5, boxSizing: 'border-box' }} />
              <button onClick={run} disabled={busy} style={{ marginTop: 8, background: 'var(--lyra-color-primary)', color: 'var(--app-on-accent)', border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Running…' : 'Run'}</button>
              {result && (
                <pre aria-label="Result" style={{ ...card, marginTop: 10, padding: 12, fontSize: 12, overflow: 'auto', maxHeight: 320, color: result.error ? 'var(--lyra-color-danger)' : 'var(--lyra-color-text)' }}>
                  {result.error ? result.error : JSON.stringify(result.output ?? result, null, 2)}
                </pre>
              )}
            </div>
          ) : doc.status === 'available' ? (
            <div style={{ marginTop: 18, fontSize: 12.5, color: 'var(--lyra-color-text-muted)' }}>
              {doc.risk !== 'read' ? 'This skill changes something outside Lyra, so it only runs inside an agent run, with your approval.' : 'This skill needs a connection or a model, so it runs inside an agent run.'}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function SkillsRegistryPage() {
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'available' | 'planned'>('all');
  const [risk, setRisk] = useState<'all' | 'read' | 'write' | 'send'>('all');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/skills-registry').then((r) => setSkills(r.data.skills)).catch((e) => setError(errorText(e, 'Could not load the skills')));
  }, []);

  const shown = useMemo(() => (skills || []).filter((s) =>
    (status === 'all' || s.status === status) && (risk === 'all' || s.risk === risk) &&
    (!q || `${s.id} ${s.name} ${s.description}`.toLowerCase().includes(q.toLowerCase()))), [skills, q, status, risk]);
  const counts = useMemo(() => ({ available: (skills || []).filter((s) => s.status === 'available').length, planned: (skills || []).filter((s) => s.status === 'planned').length }), [skills]);

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto', color: 'var(--lyra-color-text)' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Skill registry</h1>
      <p style={{ color: 'var(--lyra-color-text-muted)', margin: '4px 0 16px', fontSize: 14 }}>
        Actions agents can run through the skills service: what each does, how risky it is, and which connection it needs.
        {skills && <> <b>{counts.available}</b> available, <b>{counts.planned}</b> planned.</>}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input aria-label="Search skills" placeholder="Search skills…" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...input, minWidth: 240, flex: '1 1 240px' }} />
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as any)} style={input}><option value="all">Any status</option><option value="available">Available</option><option value="planned">Planned</option></select>
        <select aria-label="Risk" value={risk} onChange={(e) => setRisk(e.target.value as any)} style={input}><option value="all">Any risk</option><option value="read">Read</option><option value="write">Write</option><option value="send">Send</option></select>
      </div>
      {error && <div style={{ ...card, padding: 16, color: 'var(--lyra-color-danger)', fontSize: 14 }}>{error}</div>}
      {!skills && !error && <div style={{ color: 'var(--lyra-color-text-muted)' }}>Loading…</div>}
      {skills && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {shown.map((s) => (
            <button key={s.id} onClick={() => setOpen(s.id)} style={{ ...card, textAlign: 'left', padding: 14, cursor: 'pointer', color: 'inherit', font: 'inherit', opacity: s.status === 'planned' ? 0.75 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</span>
                <span style={{ display: 'flex', gap: 4 }}><span style={RISK[s.risk]}>{s.risk}</span><span style={STATUS[s.status]}>{s.status}</span></span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--lyra-color-text-muted)', margin: '6px 0 8px', lineHeight: 1.45 }}>{s.description}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {s.requirements.map((r) => <span key={r.capability} style={chip('var(--app-surface-3)', 'var(--app-text-2)')}>{r.capability}</span>)}
                {s.model && <span style={chip('var(--app-primary-soft)', 'var(--app-primary-deep)')}>model</span>}
              </div>
            </button>
          ))}
          {!shown.length && <div style={{ color: 'var(--lyra-color-text-muted)' }}>No skills match.</div>}
        </div>
      )}
      {open && <Detail id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
